import { findSuitableProduct, createProductIdFromStripeId } from './billing-utils';
import { DEFAULT_BILLING_PERIOD, DEFAULT_PRODUCT_GROUP, ROUTES } from './constants';
import { logError, addBreadcrumb, setContext } from './sentry-logger';

/**
 * @typedef {import('../types').SiteLimits} SiteLimits
 * @typedef {import('../types').ProductWithPricesAndFeatures} ProductWithPricesAndFeatures
 * @typedef {import('../types').PendingSubscription} PendingSubscription
 * @typedef {import('../types').Subscription} Subscription
 * @typedef {import('../types').User} User
 */

/**
 * @typedef {object} CheckoutRouteProps
 * @property {SiteLimits} currentSiteLimits
 * @property {Subscription} subscription
 * @property {User} user
 * @property {boolean} isTrial
 * @property {ProductWithPricesAndFeatures[]} availableProducts
 * @property {PendingSubscription} pendingSubscription
 */

/**
 * @description Get the checkout route based on the current site limits and the available
 * products. When a valid pending subscription object is passed, we use this as the base to determine
 * the checkout route.
 *
 * The checkout route is communicated to Ghost Admin, where it's used in the upgrade modals and notifications
 * to direct the customer to the correct product in the checkout page.
 * @param {CheckoutRouteProps} props
 * @returns {string}
 */
export default function getCheckoutRoute(props) {
    const {
        currentSiteLimits,
        subscription,
        user,
        isTrial,
        availableProducts,
        pendingSubscription,
    } = props;

    setContext('getCheckoutRoute', {
        currentSiteLimits,
        subscriptionId: subscription?.id || null,
        subscriptionStatus: subscription?.status || null,
        userId: user?.id || null,
        isTrial,
        isPendingSubscription: Boolean(pendingSubscription),
    });

    if (!availableProducts || availableProducts.length === 0) {
        logError(
            new Error('getCheckoutRoute: No available products provided, returning fallback route'),
            {
                level: 'warning',
            },
        );
        return ROUTES.PLANS;
    }

    if (!subscription?.meta?.baseProduct) {
        addBreadcrumb({
            message:
                'getCheckoutRoute: No base product in subscription meta, returning fallback route',
            level: 'warning',
            data: {
                subscriptionId: subscription?.id || null,
                subscriptionStatus: subscription?.status || null,
                userId: user?.id || null,
            },
        });

        return ROUTES.PLANS;
    }

    const baseProduct = subscription.meta.baseProduct;

    /** @type {string} */
    let group = user?.show_product_group || DEFAULT_PRODUCT_GROUP;

    /** @type {string} */
    let productId;
    /** @type {string} */
    let period = isTrial
        ? DEFAULT_BILLING_PERIOD
        : (baseProduct?.billing_period ?? DEFAULT_BILLING_PERIOD);

    // Handle a passed in pending subscription first. If the pending subscription is not suitable any more,
    // we have two scenarios:
    // 1. For an incomplete subscription, the customer must fulfil the pending subscription, and upgrade later if needed
    // 2. For a pending subscription, checkout will automatically pick the most suitable price based on site usage, or
    //    redirect back to plans if no suitable price for the product is found.
    if (pendingSubscription?.productId && pendingSubscription?.period) {
        try {
            const serializedProductId = createProductIdFromStripeId(pendingSubscription.productId);
            return `/checkout/${serializedProductId}-${pendingSubscription.period}`;
        } catch (error) {
            logError(error, { level: 'warning' });
            return ROUTES.PLANS;
        }
    }

    const suitableProduct = findSuitableProduct(
        availableProducts,
        currentSiteLimits,
        period,
        group,
    );

    const product = suitableProduct?.product ?? {};
    const price = suitableProduct?.price ?? {};

    if (!product.stripeId || !price.billing_period) {
        addBreadcrumb({
            message:
                'getCheckoutRoute: No suitable product or price found, returning fallback route',
            level: 'warning',
            data: {
                subscriptionId: subscription?.id || null,
                subscriptionStatus: subscription?.status || null,
                userId: user?.id || null,
                period,
                group,
            },
        });

        return ROUTES.PLANS;
    }

    try {
        productId = createProductIdFromStripeId(product.stripeId);
        period = price.billing_period;
        return `/checkout/${productId}-${period}`;
    } catch (error) {
        logError(error, { level: 'warning' });
        return ROUTES.PLANS;
    }
}
